import { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { getBusinessDateString } from '@/utils/dateHelpers';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  getRecaptureAccount, RECAPTURE_CHANGED_EVENT,
  type RecaptureAccount, type RecaptureActivity, type RecaptureRecord, type RecapturePaymentWarning
} from '@/services/recaptureService';

const currency = (value: number | null) => value === null ? 'Unavailable' : new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD'
}).format(value);

export function PendingRecaptureNotice({ records, recipientId }: { records: RecapturePaymentWarning[]; recipientId: string }) {
  const pending = records.filter(record => record.recipient_id === recipientId && record.status === 'draft');
  if (pending.length === 0) {
    return null;
  }
  return <p className="mt-2 rounded-md border border-warning/30 bg-warning/10 p-2 text-xs text-foreground" role="status">
    Pending recapture: {currency(pending.reduce((sum, record) => sum + record.target_amount, 0))}. Accounting review is needed. These drafts do not reserve balances or block payment.
  </p>;
}

export function PayoutRecaptureNotice({ records, payoutId }: { records: RecaptureRecord[]; payoutId: string }) {
  const entries = records.flatMap(record => record.allocations.filter(allocation => allocation.payout_id === payoutId).map(allocation => ({ record, allocation })));
  if (entries.length === 0) {
    return null;
  }
  return <div className="mt-1 space-y-1 text-xs text-muted-foreground">
    {entries.map(({ record, allocation }) => <p key={record.id}>
      {record.status === 'reversed' ? 'Reversed recapture' : 'Recaptured'} {currency(allocation.amount)} · EPO on {record.loan_number}
    </p>)}
  </div>;
}

export function RecaptureHistory({ records, expanded = false }: { records: RecaptureActivity[]; expanded?: boolean }) {
  const history = records.filter(record => record.status !== 'cancelled').slice().sort((a, b) =>
    (b.submitted_at ? Date.parse(b.submitted_at) : 0) - (a.submitted_at ? Date.parse(a.submitted_at) : 0)
  );
  if (history.length === 0) {
    return null;
  }
  return <details className="min-w-0" open={expanded || undefined}>
    <summary className="cursor-pointer text-sm font-medium">Recapture history</summary>
    <Table aria-label="Recapture history" containerProps={{
      className: 'mt-3 rounded-md border', role: 'region', 'aria-label': 'Recapture history table', tabIndex: 0
    }}>
      <TableHeader><TableRow>
        <TableHead scope="col">Loan</TableHead>
        <TableHead scope="col">Reason</TableHead>
        <TableHead scope="col">Posted</TableHead>
        <TableHead scope="col" className="text-right">Recovered from commissions</TableHead>
        <TableHead scope="col" className="text-right">Absorbed by profitability</TableHead>
        <TableHead scope="col" className="text-right">Total</TableHead>
        <TableHead scope="col">Status</TableHead>
      </TableRow></TableHeader>
      <TableBody>{history.map(record => {
        const commission = record.allocations.filter(allocation => allocation.kind === 'commission').reduce((sum, allocation) => sum + allocation.amount, 0);
        const profitability = record.allocations.filter(allocation => allocation.kind === 'profitability').reduce((sum, allocation) => sum + allocation.amount, 0);
        return <TableRow key={record.id}>
          <TableCell className="whitespace-nowrap font-medium">{record.loan_number}</TableCell>
          <TableCell>{record.reason.toUpperCase()}</TableCell>
          <TableCell className="whitespace-nowrap">{record.submitted_at ? format(parseISO(getBusinessDateString(parseISO(record.submitted_at))), 'MMM d, yyyy') : '—'}</TableCell>
          <TableCell className="text-right tabular-nums whitespace-nowrap">{record.status === 'draft' ? '—' : currency(commission)}</TableCell>
          <TableCell className="text-right tabular-nums whitespace-nowrap">{record.status === 'draft' ? '—' : currency(profitability)}</TableCell>
          <TableCell className="text-right tabular-nums whitespace-nowrap">{currency(record.target_amount)}</TableCell>
          <TableCell>
            {record.status === 'reversed' ? 'Reversed' : record.status === 'draft' ? 'Draft' : 'Submitted'}
            {record.status === 'reversed' && record.reversal_reason && <p className="mt-1 text-xs text-muted-foreground">Reversal: {record.reversal_reason}</p>}
          </TableCell>
        </TableRow>;
      })}</TableBody>
    </Table>
  </details>;
}

/** Shares the view's account response; lifetime balance remains distinct from period totals. */
export function RecaptureAccountPanel({ account, error = false, onRetry, onHistory }: {
  account: RecaptureAccount | null;
  error?: boolean;
  onRetry: () => void;
  onHistory?: () => void;
}) {
  if (!error && !account) {
    return null;
  }
  return <section className={onHistory ? 'mc-detail-card mc-recapture-panel' : 'min-w-0 space-y-3 rounded-md border bg-card p-4'} aria-label="Recapture and available profitability">
    {onHistory && <h2 className="mc-panel-heading">Recapture &amp; available profitability</h2>}
    {error && <p role="status" className="text-sm text-muted-foreground">Recapture activity unavailable. {account && 'Previously loaded history is shown. '}<Button size="sm" variant="outline" onClick={onRetry}>Retry</Button></p>}
    {account && <div className="flex flex-wrap justify-between gap-3 text-sm">
      <p>Available LO Profitability <strong>{currency(account.profitability.available)}</strong></p>
      {(account.profitability.bonuses ?? 0) !== 0 && <p>Profitability bonuses <strong>{currency(account.profitability.bonuses ?? 0)}</strong></p>}
      <p>Absorbed by recapture <strong>{currency(account.profitability.absorbed)}</strong></p>
      {(account.profitability.volume_bonus_committed ?? 0) > 0 && <p>Committed to volume bonuses <strong>{currency(account.profitability.volume_bonus_committed ?? 0)}</strong></p>}
    </div>}
    {account && !account.profitability.complete && <p className="text-xs text-muted-foreground">Historical profitability needs review before this balance can be used.</p>}
    {onHistory ? (account?.records.some(record => record.status !== 'cancelled') && <Button variant="ghost" className="mc-panel-link" onClick={onHistory}>
      <span>Recapture history</span> <span>{account.records.filter(record => record.status !== 'cancelled').length} ›</span>
    </Button>) : <RecaptureHistory records={account?.records ?? []} />}
  </section>;
}

export function RecaptureAccountSummary({ recipientId, onHistory, onAccountChange }: {
  recipientId: string;
  onHistory?: () => void;
  onAccountChange?: (account: RecaptureAccount | null, error: boolean) => void;
}) {
  const [account, setAccount] = useState<{ recipientId: string; data: RecaptureAccount | null } | null>(null);
  const [error, setError] = useState(false);
  const [loadedRecipient, setLoadedRecipient] = useState('');
  const [refreshVersion, setRefreshVersion] = useState(0);
  useEffect(() => {
    if (loadedRecipient === recipientId) {
      onAccountChange?.(account?.recipientId === recipientId ? account.data : null, error);
    }
  }, [account, error, loadedRecipient, recipientId, onAccountChange]);
  useEffect(() => {
    let active = true;
    let request = 0;
    const update = () => {
      const current = ++request;
      void getRecaptureAccount(recipientId).then(result => {
        if (active && current === request) {
          setAccount({ recipientId, data: result });
          setLoadedRecipient(recipientId);
          setError(false);
        }
      }).catch(() => {
        if (active && current === request) {
          setLoadedRecipient(recipientId);
          setError(true);
        }
      });
    };
    update();
    window.addEventListener(RECAPTURE_CHANGED_EVENT, update);
    return () => {
      active = false;
      window.removeEventListener(RECAPTURE_CHANGED_EVENT, update);
    };
  }, [recipientId, refreshVersion]);
  if (loadedRecipient !== recipientId) {
    return null;
  }
  return <RecaptureAccountPanel
    account={account?.recipientId === recipientId ? account.data : null}
    error={error}
    onRetry={() => setRefreshVersion(value => value + 1)}
    onHistory={onHistory}
  />;
}
